<template>
  <a
    v-if="displayAsButton"
    :href="actionUrl(buttonActions[0])"
    :class="['btn btn-sm', btnCssClass, actionDisabled(buttonActions[0]) ? 'disabled' : null, 'me-2']"
    @click="onItemAction($event, buttonActions[0])"
  >
    {{ buttonActions[0].title }}
  </a>
  <div
    v-else-if="displayAsDropdown"
    class="btn-group me-2"
  >
    <template v-if="dropdownAsSplitButton">
      <a
        :href="actionUrl(buttonActions[0])"
        :class="['btn btn-sm', btnCssClass, actionDisabled(buttonActions[0]) ? 'disabled' : null]"
        @click="onItemAction($event, buttonActions[0])"
      >
        {{ buttonActions[0].title }}
      </a>
      <button
        type="button"
        :class="['btn btn-sm', btnCssClass, 'dropdown-toggle dropdown-toggle-split']"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        <span class="visually-hidden">Toggle Dropdown</span>
      </button>
    </template>
    <button
      v-else
      :class="['btn btn-sm', btnCssClass, 'dropdown-toggle']"
      type="button"
      data-bs-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
    >
      {{ buttonActions[0].title }}
    </button>
    <div :class="['dropdown-menu', {'dropdown-menu-end': dropdownAsSplitButton}]">
      <template
        v-for="(action, idx) of buttonActions.slice(1)"
        :key="idx"
      >
        <div
          v-if="action.type == 3"
          class="dropdown-divider"
        />
        <a
          v-else
          :href="actionUrl(action)"
          :class="['dropdown-item', actionDisabled(action) ? 'disabled' : null]"
          @click="onItemAction($event, action)"
        >
          {{ action.title }}
        </a>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed} from 'vue'
import {isNonEmptyString} from '@/js/js-utils'
import {ButtonActionType} from './types'
import type {ButtonAction, GridID} from './types'

const props = defineProps({
  actions: {default: null, type: [Object, Array]}, //  as () => ButtonAction[] | ButtonAction | null
  selectedItemIds: {default: (): GridID[] => [], type: Array as () => GridID[]},
})

const emit = defineEmits<{
  (e: 'item-action', action: ButtonAction): void
}>()

const buttonActions = computed((): ButtonAction[] => {
  const result: ButtonAction[] = []
  // ensure that itemActions is array
  const itemActions = Array.isArray(props.actions) ? props.actions : (props.actions ? [props.actions] : [])

  let skipSeparator = true
  for (const action of itemActions) {
    if (action.type == ButtonActionType.separator) {
      if (!skipSeparator) {
        result.push(action)
        // don't want two separators in a row
        skipSeparator = true
      }
    } else {
      result.push(action)

      // we don't need a separator after first item
      // because that will be a first item in the dropdown menu
      skipSeparator = result.length > 1
    }
  }

  return result
})

const displayAsButton = computed((): boolean => {
  return buttonActions.value.length == 1
})

const displayAsDropdown = computed((): boolean => {
  return buttonActions.value.length > 1
})

const dropdownAsSplitButton = computed((): boolean => {
  return buttonActions.value.length > 0 && isNonEmptyString(buttonActions.value[0].url)
})

const btnCssClass = computed((): string => {
  if (buttonActions.value.length == 0) {
    return ''
  }

  const mainAction = buttonActions.value[0]
  if (isNonEmptyString(mainAction.cssClass)) {
    return mainAction.cssClass
  }

  return 'btn-primary'
})

function emitAction(action: ButtonAction | null): void {
  if (action && action.type == ButtonActionType.action) {
    emit('item-action', action)
  }
}

function onItemAction(e: Event, action: ButtonAction): void {
  if (action.type == ButtonActionType.url) {
    // don't do anything for URL's
    return
  }

  e.preventDefault()
  emitAction(action)
}

function actionUrl(action: ButtonAction): string {
  return action.type == ButtonActionType.url ? action.url : '#'
}

function actionDisabled(action: ButtonAction): boolean {
  if (action.onlyWithSelectedItems != true) {
    return false
  }
  return props.selectedItemIds.length == 0
}
</script>
